import React, { useState, useMemo } from "react";
import {
  Collapse,
  Button,
  Space,
  Input,
  Select,
  Empty,
  Pagination,
} from "antd";
import { EyeOutlined, PhoneOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { calculateInterventions, getContactInfo } from "../utils/interventionCalculations";

const AllOrders = ({ orders, loading, onViewOrder, onRefresh }) => {
  const [filteredOrders, setFilteredOrders] = useState(orders);
  const [businessFilter, setBusinessFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const businesses = useMemo(() => {
    return [
      ...new Set(
        orders.map((order) => order.restaurant?.businessInfo?.name).filter(Boolean)
      ),
    ];
  }, [orders]);

  React.useEffect(() => {
    let filtered = orders;

    if (businessFilter) {
      filtered = filtered.filter(
        (order) => order.restaurant?.businessInfo?.name === businessFilter
      );
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [orders, businessFilter]);

  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, currentPage, pageSize]);

  const renderCollapseItems = () => {
    return paginatedOrders.map((order, index) => {
      const intervention = calculateInterventions(order);
      const contactInfo = intervention.issue ? getContactInfo(order, intervention.issue) : null;

      return {
        key: order.id,
        label: (
          <div className="flex justify-between items-center w-full pr-4">
            <div className="flex gap-8 flex-1">
              <div>
                <p className="text-xs text-gray-600">Order Time</p>
                <p className="font-medium">
                  {new Date(order.createdAt).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Ref Code</p>
                <p className="font-bold">#{order.orderCode}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Issue</p>
                <p className="font-medium">
                  {intervention.issue || "None"}
                </p>
              </div>
            </div>
          </div>
        ),
        children: (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 font-semibold mb-1">
                  Business:
                </p>
                <p className="text-gray-900">
                  {order.restaurant?.businessInfo?.name || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-semibold mb-1">
                  Customer:
                </p>
                <p className="text-gray-900">
                  {order.user?.firstName} {order.user?.lastName}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="mb-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Order Type:</span>
                <span className="ml-2 text-sm font-medium text-gray-900 bg-gray-100 px-2 py-1 rounded">
                  {order.deliveryType === "DELIVERY" ? "Delivery" : order.deliveryType === "PICKUP" ? "Pickup" : (order.deliveryType || "N/A")}
                </span>
              </div>
              <div className="flex gap-4">
                <Space>
                  {contactInfo && (
                    <>
                      <Button
                        type="default"
                        icon={<PhoneOutlined />}
                        size="small"
                        onClick={() => {
                          window.open(`tel:${contactInfo.phone}`);
                        }}
                      >
                        Call
                      </Button>
                      <Button
                        icon={<WhatsAppOutlined />}
                        size="small"
                        onClick={() => {
                          window.open(
                            `https://wa.me/${contactInfo.phone}?text=Regarding order ${order.orderCode}`
                          );
                        }}
                        style={{ color: "#25D366" }}
                      >
                        WhatsApp
                      </Button>
                    </>
                  )}
                  <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => onViewOrder(order)}
                  >
                    View Order
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        ),
      };
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading orders...</div>;
  }

  if (orders.length === 0) {
    return (
      <Empty
        description="No orders found"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <Select
          placeholder="Filter by business"
          allowClear
          value={businessFilter || undefined}
          onChange={setBusinessFilter}
          style={{ width: "200px" }}
        >
          {businesses.map((business) => (
            <Select.Option key={business} value={business}>
              {business}
            </Select.Option>
          ))}
        </Select>
        <Button onClick={onRefresh}>Refresh</Button>
      </div>

      {filteredOrders.length === 0 ? (
        <Empty description="No orders match the filter" />
      ) : (
        <>
          <Collapse items={renderCollapseItems()} accordion={false} />
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filteredOrders.length}
            onChange={setCurrentPage}
            onShowSizeChange={(_, size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            showSizeChanger
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} orders`
            }
          />
        </>
      )}
    </div>
  );
};

export default AllOrders;
